import { memo, useEffect, useRef, useState } from 'react'
import type { LeaderboardEntry } from '@gymboard/types'
import { TEAM_PRESETS } from '@gymboard/types'
import { ICON_PHONE, ICON_SYNC, ZoneChip } from './SpotlightStage'

/** Above this the list still fits, but rows shrink to stay on screen; the
 *  TV can't scroll, so past MAX_ROWS the tail collapses into one line. */
const MIN_SLOTS = 8
const MAX_ROWS = 24
/** Past this many athletes the chorus splits into two columns, so rows
 *  stay readable from across the floor instead of shrinking. */
const TWO_COLUMNS_ABOVE = 12

/** Podium colours: camel, wool-silver, bronze. No gold in the system. */
const PODIUM = ['#D9B68A', '#B4B3C2', '#C98B5A']

const BATCHED_HR_SOURCES = new Set(['apple_health', 'android_health'])

/** Phone-motion athletes and delayed Health feeds get a small marker so the
 *  room knows the number is a proxy or lagging (same rules as before). */
export function sourceMarker(e: LeaderboardEntry): 'phone' | 'sync' | null {
  if (e.hrSource === 'phone_imu') return 'phone'
  if (e.hrSource && BATCHED_HR_SOURCES.has(e.hrSource) && e.hrStale) return 'sync'
  return null
}

const Row = memo(function Row({
  entry,
  index,
  rowPx,
  on,
  cols,
  perCol,
  final,
}: {
  entry: LeaderboardEntry
  index: number
  rowPx: number
  on: boolean
  cols: 1 | 2
  /** Class over: no live BPM column, nobody dimmed for going quiet. */
  final: boolean
  /** Rows in each column (fill order: down the first column, then the second). */
  perCol: number
}) {
  const team = entry.team != null ? TEAM_PRESETS[entry.team] : undefined
  const marker = sourceMarker(entry)
  const isPhone = marker === 'phone' && entry.effortScore != null
  const font = Math.max(14, Math.min(rowPx * 0.4, cols === 2 ? 22 : 34))
  // Half-width rows: "Devon W." instead of a truncated "Dev…".
  const parts = entry.athleteName.trim().split(/\s+/)
  const name = cols === 2 && parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : entry.athleteName
  // Two columns fill down the first, then the second (rank order reads
  // top-to-bottom, left column first). Position is transform-only.
  const col = cols === 2 ? Math.floor(index / perCol) : 0
  const row = cols === 2 ? index % perCol : index
  return (
    <div
      className={`sp-row${on ? ' on' : ''}${entry.isActive || final ? '' : ' off'}`}
      style={{
        height: rowPx,
        width: cols === 2 ? 'calc(50% - 0.4vw)' : undefined,
        right: cols === 2 ? 'auto' : undefined,
        transform: `translate(${col === 1 ? 'calc(100% + 0.8vw)' : '0'}, ${row * rowPx}px)`,
        fontSize: font,
      }}
      data-athlete-id={entry.athleteId}
    >
      <span className="sp-rk" style={entry.rank <= 3 ? { color: PODIUM[entry.rank - 1] } : undefined}>{entry.rank}</span>
      <span className="sp-nm">
        <span data-chorus-name={entry.athleteId} title={entry.athleteName}>{name}</span>
        {team && (
          <span className="sp-team" style={{ background: team.color }} aria-label={`Team ${team.name}`}>
            {team.name[0]}
          </span>
        )}
        {marker && (
          <span className={`sp-src ${marker}`} aria-label={marker === 'phone' ? 'Phone motion, no heart-rate sensor' : 'Heart rate delayed'}>
            {marker === 'phone' ? ICON_PHONE : ICON_SYNC}
          </span>
        )}
      </span>
      <ZoneChip zone={entry.hrZone} className="" />
      {cols === 1 && !final && (
        <span className="sp-bpm" style={{ fontSize: font * 0.72 }}>
          {isPhone ? Math.round(entry.effortScore ?? 0) : entry.heartRate ? Math.round(entry.heartRate) : '—'}
        </span>
      )}
      <span className="sp-sc" style={cols === 2 ? { width: '2.4em' } : undefined}>{Math.round(entry.score)}</span>
    </div>
  )
})

interface Props {
  entries: LeaderboardEntry[]
  spotlightId: string | null
  /** End-of-class standings (see Row). */
  final?: boolean
}

/**
 * The chorus: everyone else, ranked, beside the stage. Rows keep their DOM
 * node and travel to their new rank (transform only), so an overtake reads
 * as movement rather than a re-sort.
 */
export default function ChorusList({ entries, spotlightId, final = false }: Props) {
  const listRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const measure = () => setHeight(el.clientHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const shown = entries.slice(0, MAX_ROWS)
  const hidden = entries.length - shown.length
  const cols: 1 | 2 = shown.length > TWO_COLUMNS_ABOVE ? 2 : 1
  const perCol = Math.ceil(shown.length / cols)
  const rowPx = height > 0 ? height / Math.max(perCol, MIN_SLOTS) : 0
  // Stable DOM order (by id) so React never re-mounts a row on a rank change;
  // position comes from the transform.
  // Live: stable (id) DOM order so rows can travel to their new rank.
  // Final standings don't move, so render them in rank order for screen readers.
  const ordered = final ? shown : [...shown].sort((a, b) => (a.athleteId < b.athleteId ? -1 : 1))
  const indexOf = new Map(shown.map((e, i) => [e.athleteId, i]))
  const active = entries.filter((e) => e.isActive).length

  return (
    <div className="sp-chorus">
      <div className="sp-chorus-head">
        <span>{final ? 'Final standings' : 'Leaderboard'}</span>
        <span>{final || active === entries.length ? `${entries.length} ${entries.length === 1 ? 'athlete' : 'athletes'}` : `${active} of ${entries.length} active`}</span>
      </div>
      <div className="sp-list" ref={listRef}>
        {rowPx > 0 &&
          ordered.map((e) => (
            <Row key={e.athleteId} entry={e} index={indexOf.get(e.athleteId) ?? 0} rowPx={rowPx} on={e.athleteId === spotlightId} cols={cols} perCol={perCol} final={final} />
          ))}
      </div>
      {hidden > 0 && <div className="sp-more">+{hidden} more in this class</div>}
    </div>
  )
}
